import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export {dayjs};

/**
 * Converts the given seconds to a days/hours/minutes phrase.
 */
function secondsToDaysHoursMinutes(seconds: number, includeSeconds = false) {
  const SECONDS_PER_MINUTE = 60;
  const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * 60;
  const SECONDS_PER_DAY = SECONDS_PER_HOUR * 24;
  const days = Math.floor(seconds / SECONDS_PER_DAY);
  const hours = Math.floor(
    (seconds - days * SECONDS_PER_DAY) / SECONDS_PER_HOUR
  );
  const minutes = Math.floor(
    (seconds - (hours * SECONDS_PER_HOUR + days * SECONDS_PER_DAY)) /
      SECONDS_PER_MINUTE
  );
  const remainingSeconds =
    seconds -
    (hours * SECONDS_PER_HOUR +
      days * SECONDS_PER_DAY +
      minutes * SECONDS_PER_MINUTE);

  const daysHoursMins = [];

  if (days > 0) {
    daysHoursMins.push(`${days}${getLabel(days, 'day')}`);
  }

  if (hours > 0) {
    daysHoursMins.push(`${hours}${getLabel(hours, 'hr')}`);
  }

  if (minutes > 0) {
    daysHoursMins.push(`${minutes}${getLabel(minutes, 'min')}`);
  }

  if (includeSeconds && remainingSeconds) {
    daysHoursMins.push(
      `${remainingSeconds}${getLabel(remainingSeconds, 'sec')}`
    );
  }

  return daysHoursMins.join(' ');

  function getLabel(value: number, label: string) {
    if (value > 1) {
      return ` ${label}s`;
    }
    if (value === 1) {
      return ` ${label}`;
    }
    return '';
  }
}

/**
 * Converts the given seconds to a days/hours/minutes phrase.
 */
export function timestampsToDaysHoursMinutes(ts1: string, ts2: string) {
  const start = dayjs(ts1);
  const end = dayjs(ts2);
  const durationInSeconds = end.diff(start, 'second');
  return secondsToDaysHoursMinutes(durationInSeconds);
}

/**
 * Converts the given UTC timestamp into a formatted local time string.
 */
export function utcToLocalDateTime(
  utcTimestamp?: string | number,
  includeSeconds = false
) {
  let dateString;

  if (!utcTimestamp) {
    return '';
  }

  const ts =
    typeof utcTimestamp === 'string' || typeof utcTimestamp === 'number'
      ? dayjs.utc(utcTimestamp).local()
      : utcTimestamp;

  const today = dayjs();

  if (ts.isSame(today, 'day')) {
    dateString = 'Today';
  } else if (today.subtract(1, 'day').isSame(ts, 'day')) {
    dateString = 'Yesterday';
  } else {
    dateString = ts.format('M/D/YY');
  }

  return `${dateString} ${ts.format(`h:mm${includeSeconds ? ':ss' : ''} A`)}`;
}

/**
 * Converts a given UTC time into a local time "h:mm A" format.
 */
export function utcToLocalTimestamp(
  timeString: string,
  includeSeconds = false
) {
  return dayjs
    .utc(timeString)
    .local()
    .format(`h:mm${includeSeconds ? ':ss' : ''} A`);
}
